<script setup lang="ts">
import type { PeriodI } from "@lxc/app-device-common";
import dayjs from "dayjs";
import { type Ref, ref } from "vue";
import { Filters } from "~/core/models/filters";
import { formatIsoDate, getPeriod } from "~/core/utils/date";
import type LogFilters from "~/modules/log/components/LogFilters.vue";
import { useFetchLogs } from "~/modules/log/composables/useFetchLogs";
import type { AsyncLog } from "~/modules/log/models/AsyncLog.interface";
import type { LogEntity } from "~/modules/log/models/LogEntity.interface";
import { LogType } from "~/modules/log/models/LogType.enum";

const { t } = useI18n();
const props = defineProps<{
  businessUnitCode?: number;
  organizationCode?: number;
  pathContext?: string;
}>();

const {
  error,
  fetchData,
  filters,
  isLoading,
  results,
  setFilter,
  search,
  onSearch,
} = useFetchLogs({
  businessUnitCode: props.businessUnitCode,
  organizationCode: props.organizationCode,
});
const dropdownLink: Ref<HTMLLinkElement | undefined | null> = ref();
const logFiltersRef: Ref<typeof LogFilters | undefined> = ref();
const rowsSelected: Ref<AsyncLog[]> = ref([]);

const dateFormat: string = t("logs.list.column.timestamp.format");

function addRequiredFilters() {
  if (!filters.get(Filters.LOG_TYPE)?.value) {
    setFilter(Filters.LOG_TYPE, LogType.AUDIT);
  }

  if (
    !(
      filters.get(Filters.LOG_START_DATE)?.value &&
      filters.get(Filters.LOG_END_DATE)?.value
    )
  ) {
    const todayPeriod: PeriodI = getPeriod();

    if (!filters.get(Filters.LOG_START_DATE)?.value) {
      setFilter(Filters.LOG_START_DATE, dayjs(todayPeriod.startDate).format());
    }
    if (!filters.get(Filters.LOG_END_DATE)?.value) {
      setFilter(Filters.LOG_END_DATE, dayjs(todayPeriod.endDate).format());
    }
  }
}

async function loadData(page?: number, pageSize?: number) {
  addRequiredFilters();
  await fetchData(page, pageSize);
  dropdownLink.value = null;
}

const onPageChange = async (page?: number, pageSize?: number) => {
  await loadData(page, pageSize);
};

const openDropdown = (element?: HTMLLinkElement | null) => {
  dropdownLink.value = element;
};

const onApplyEntityAsAFilter = async (logEntity: LogEntity) => {
  setFilter(Filters.LOG_AUTHOR_CLASS, logEntity.entityClass ?? "");
  setFilter(
    Filters.LOG_AUTHOR_ID,
    logEntity.entityId ? [logEntity.entityId] : [],
  );
  await search();
};

onSearch(loadData);
</script>

<template>
  <log-filters
    ref="logFiltersRef"
    :filters="filters"
    :logs="results?.results"
    :rows-selected="rowsSelected"
    :business-unit-code="businessUnitCode"
    :organization-code="organizationCode"
    @change="setFilter"
    @enter="search"
  />
  <container-component :px="0" :py="4">
    <lxc-table
      min-width="86rem"
      :empty-text="t('logs.list.empty')"
      :data="results?.results"
      :context="results?.pagination"
      :is-loading="isLoading"
      :error="error?.toError()"
      @change-page-and-page-size="onPageChange"
      @select="rowsSelected = $event"
    >
      <lxc-table-column type="selection" />
      <lxc-table-column
        align="left"
        :label="t('logs.timestamp.label')"
        width="13rem"
      >
        <template #default="scope">
          {{ formatIsoDate(scope.row.timestamp, dateFormat) }}
        </template>
      </lxc-table-column>
      <lxc-table-column
        align="left"
        :label="t('logs.level.label')"
        width="10rem"
      >
        <template #default="scope">
          {{ t(`logs.level.value.${scope.row.level}`) }}
        </template>
      </lxc-table-column>
      <lxc-table-column :label="t('logs.eventType.label')" width="10rem">
        <template #default="scope">
          {{ t(`logs.eventType.value.${scope.row.eventType}`) }}
        </template>
      </lxc-table-column>
      <lxc-table-column
        :label="t('logs.list.column.label.label')"
        prop="label"
        min-width="24rem"
      >
        <template #default="scope">
          <log-label
            v-model="scope.row"
            :path-context="pathContext"
            @click="openDropdown"
          />
        </template>
      </lxc-table-column>
      <lxc-table-column
        prop="actionTypeTranslated"
        :label="t('logs.actionType.label')"
        width="8rem"
      >
        <template #default="scope">
          {{ scope.row.actionTypeTranslated || scope.row.actionType }}
        </template>
      </lxc-table-column>
      <lxc-table-column
        :label="t('logs.origin.label')"
        class="!py-0"
        width="6rem"
      >
        <template #default="scope">
          <log-origin :model-value="scope.row.origin" />
        </template>
      </lxc-table-column>
      <lxc-table-column
        prop="actionTranslated"
        :label="t('logs.action.label')"
        width="15rem"
      >
        <template #default="scope">
          {{ scope.row.actionTranslated || scope.row.action }}
        </template>
      </lxc-table-column>
    </lxc-table>
  </container-component>
  <log-dropdown
    :element="dropdownLink"
    @apply-as-a-filter="onApplyEntityAsAFilter"
  />
</template>
